import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "credentialId",
    "credentialRawId",
    "credentialType",
    "credentialAttestationObject",
    "credentialClientDataJson",
    "submitButton"
  ]

  static values = {
    challengeUrl: String
  }

  async setupPasskey() {
    try {
      if (!this.checkWebAuthnSupport('create')) return

      // Disable button while fetching challenge data
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = '⏳ Creating Passkey...'

      const challengeUrl = this.getChallengeUrl()
      // Fetch challenge from backend
      const challengeData = await this.fetchWithCSRF(challengeUrl)

      const credential = await navigator.credentials.create({
        publicKey: this.buildCredentialCreationOptions(challengeData)
      })

      // Populate form hidden fields with credential data
      this.credentialIdTarget.value = credential.id
      this.credentialRawIdTarget.value = this.arrayBufferToBase64url(credential.rawId)
      this.credentialTypeTarget.value = credential.type
      this.credentialAttestationObjectTarget.value = this.arrayBufferToBase64url(credential.response.attestationObject)
      this.credentialClientDataJsonTarget.value = this.arrayBufferToBase64url(credential.response.clientDataJSON)

      this.element.requestSubmit()

    } catch (error) {
      // Re-enable button on error
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = '🔐 Create Passkey'
      alert(error.message)
    }
  }

  async authenticate() {
    try {
      if (!this.checkWebAuthnSupport('get')) return

      const challengeData = await this.fetchWithCSRF('/users/sign_in/new_challenge')
      const credential = await navigator.credentials.get({
        publicKey: this.buildCredentialRequestOptions(challengeData)
      })

      const result = await this.fetchWithCSRF('/users/sign_in', {
        user: { passkey_credential: this.formatCredentialForAuthentication(credential) }
      })

      this.handleSuccess(result, 'Authentication')

    } catch (error) {
      alert(error.message)
    }
  }

  checkWebAuthnSupport(operation) {
    if (!navigator.credentials[operation]) {
      alert('WebAuthn is not supported in this browser')
      return false
    }
    return true
  }

  getChallengeUrl() {
    // Use the value from data attribute if present, otherwise use default
    return this.hasChallengeUrlValue ? this.challengeUrlValue : '/users/account/passkeys/new_challenge'
  }

  async fetchWithCSRF(url, body = null) {
    const options = {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
    }
    if (body) {
      options.headers['Content-Type'] = 'application/json'
      options.body = JSON.stringify(body)
    }

    const response = await fetch(url, options)
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message)
    }
    return response.json()
  }

  buildCredentialCreationOptions(challengeData) {
    return {
      challenge: this.base64urlToArrayBuffer(challengeData.challenge),
      rp: challengeData.rp,
      user: {
        ...challengeData.user,
        id: new TextEncoder().encode(challengeData.user.id)
      },
      pubKeyCredParams: challengeData.pubKeyCredParams,
      timeout: challengeData.timeout,
      excludeCredentials: challengeData.excludeCredentials?.map(cred => ({
        id: this.base64urlToArrayBuffer(cred.id),
        type: cred.type
      })),
      authenticatorSelection: challengeData.authenticatorSelection,
      attestation: challengeData.attestation
    }
  }

  buildCredentialRequestOptions(challengeData) {
    return {
      challenge: this.base64urlToArrayBuffer(challengeData.challenge),
      timeout: 60000,
      allowCredentials: challengeData.allowCredentials?.map(cred => ({
        id: this.base64urlToArrayBuffer(cred.id),
        type: cred.type
      })),
      userVerification: challengeData.userVerification
    }
  }

  formatCredentialForAuthentication(credential) {
    return {
      id: credential.id,
      rawId: this.arrayBufferToBase64url(credential.rawId),
      response: {
        authenticatorData: this.arrayBufferToBase64url(credential.response.authenticatorData),
        clientDataJSON: this.arrayBufferToBase64url(credential.response.clientDataJSON),
        signature: this.arrayBufferToBase64url(credential.response.signature),
        userHandle: credential.response.userHandle ? this.arrayBufferToBase64url(credential.response.userHandle) : null
      },
      type: credential.type
    }
  }

  handleSuccess(result, operation) {
    if (result.status === 'ok') {
      window.location.href = result.redirect_url
    } else {
      alert(`${operation} failed: ${result.message}`)
    }
  }

  // Helper functions for base64url conversion
  base64urlToArrayBuffer(base64url) {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
    const binary = atob(padded)
    return Uint8Array.from(binary, c => c.charCodeAt(0)).buffer
  }
  
  arrayBufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer)
    const binary = String.fromCharCode(...bytes)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }
}

